import { z } from 'zod'

/**
 * X 競合一覧 全 API のリクエスト/レスポンス型・Zod スキーマ（FE のバリデーションでも共有する想定）。
 */

// 範囲指定の上限（最大9桁）。FE と共有する想定のため共通定数化
export const MAX_NUMERIC = 999999999

export const MAX_RANGE_DAYS = 184
export const MAX_RANGE_MS = MAX_RANGE_DAYS * 24 * 60 * 60 * 1000

// ページネーションで 1 ページに表示可能なデータ件数の選択肢。FE と共有する想定のため共通定数化
export const PAGE_SIZE_OPTIONS = [10, 25, 50] as const
// ページネーションのデフォルト表示件数
export const DEFAULT_PAGE_SIZE = 25

// CSV ダウンロードで 1 ページに取得する最大件数（FE のページングループが 1 リクエストで取る上限）
export const CSV_PAGE_SIZE = 1000
// CSV ダウンロードの総取得件数上限（暴走防止。応募者エクスポート 50000 件に揃える）
export const CSV_MAX_TOTAL = 50000

// アカウントリモートサーチ（ID 指定）で一度に解決できる accountIds の上限（暴走防止）
export const ACCOUNT_SEARCH_IDS_MAX = 50

const benchmarkPostsQueryBaseShape = {
  groupId: z.coerce.number().int().positive(),
  // 期間（ISO8601、差分最大6ヶ月・未来日不可はビジネスロジックで検証）
  startDatetime: z.string().datetime({ offset: true }),
  endDatetime: z.string().datetime({ offset: true }),
  industryIds: z.array(z.coerce.number().int().nonnegative()).optional(),
  accountIds: z.array(z.coerce.number().int().positive()).optional(),
  campaignTypes: z.array(z.coerce.number().int().min(0).max(9)).optional(),
  incentives: z.array(z.coerce.number().int().min(0).max(7)).optional(),
  winnerCountMin: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  winnerCountMax: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  followersMin: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  followersMax: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  keyword: z.string().max(60).optional(),
  impressionsCountMin: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  impressionsCountMax: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  likesCountMin: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  likesCountMax: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  retweetsCountMin: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  retweetsCountMax: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  quotesCountMin: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  quotesCountMax: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  repliesCountMin: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  repliesCountMax: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  reactionsCountMin: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  reactionsCountMax: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  // 反応率（%）は 0〜100 の範囲のみを許容する（他の指標は件数のため MAX_NUMERIC が上限）
  reactionsRateMin: z.coerce.number().nonnegative().max(100).optional(),
  reactionsRateMax: z.coerce.number().nonnegative().max(100).optional(),
  campaignDaysMin: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  campaignDaysMax: z.coerce.number().int().nonnegative().max(MAX_NUMERIC).optional(),
  // クエリ文字列では "true"/"false"/"1"/"0" が来るため coerce.boolean ではなく明示変換
  isTrend: z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform(v => v === true || v === 'true' || v === '1')
    .optional(),
  // 当選人数が判別不可（＝AIが記載を検出できず 0 を採番）の投稿のみに絞り込む。ON 時は winnerCountMin/Max を無視する
  winnerCountUndetectable: z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform(v => v === true || v === 'true' || v === '1')
    .optional(),
  tags: z.array(z.coerce.number().int().positive()).optional(),
  sortBy: z
    .enum([
      'id',
      'lastSyncedAt',
      'campaignStartDatetime',
      'campaignEndDatetime',
      'impressionsCount',
      'likesCount',
      'retweetsCount',
      'quotesCount',
      'repliesCount',
      'reactionsCount',
      'reactionsRate',
      'winnerCount',
      'campaignDays'
    ])
    .default('campaignStartDatetime'),
  order: z.enum(['asc', 'desc']).default('desc'),
  offset: z.coerce.number().int().nonnegative().default(0)
} as const

const refineBenchmarkPostsQuery = (
  data: {
    startDatetime: string
    endDatetime: string
  } & Record<string, unknown>,
  ctx: z.RefinementCtx
): void => {
  {
    const start = new Date(data.startDatetime)
    const end = new Date(data.endDatetime)
    const now = new Date()

    // 期間の前後関係: endDatetime は startDatetime より後
    if (end < start) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDatetime'],
        message: '終了日時は、開始日時より後を指定してください。'
      })
    }

    // 未来日不可: startDatetime・endDatetime は現在時刻以前
    if (start > now) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startDatetime'],
        message: '開始日時に未来の日時は指定できません。'
      })
    }
    if (end > now) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDatetime'],
        message: '終了日時に未来の日時は指定できません。'
      })
    }

    const diffMs = end.getTime() - start.getTime()
    if (diffMs > MAX_RANGE_MS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDatetime'],
        message: '期間は最大6ヶ月以内で指定してください。'
      })
    }
    // Min/Max ペアの範囲チェック（Min > Max でエラー）
    const rangePairs: { min: string; max: string; label: string }[] = [
      { min: 'winnerCountMin', max: 'winnerCountMax', label: '当選人数' },
      { min: 'followersMin', max: 'followersMax', label: 'フォロワー数' },
      { min: 'impressionsCountMin', max: 'impressionsCountMax', label: 'インプレッション数' },
      { min: 'likesCountMin', max: 'likesCountMax', label: 'いいね数' },
      { min: 'retweetsCountMin', max: 'retweetsCountMax', label: 'リポスト数' },
      { min: 'quotesCountMin', max: 'quotesCountMax', label: '引用数' },
      { min: 'repliesCountMin', max: 'repliesCountMax', label: '返信数' },
      { min: 'reactionsCountMin', max: 'reactionsCountMax', label: '反応数' },
      { min: 'reactionsRateMin', max: 'reactionsRateMax', label: '反応率' },
      { min: 'campaignDaysMin', max: 'campaignDaysMax', label: '開催日数' }
    ]
    for (const { min, max, label } of rangePairs) {
      const minVal = data[min]
      const maxVal = data[max]
      if (typeof minVal === 'number' && typeof maxVal === 'number' && minVal > maxVal) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [min],
          message: `${label}の値が不正です。`
        })
      }
    }
  }
}

// GET / 競合投稿一覧: フィルタ + ページング（limit は表示件数プリセットに限定）
export const getTwitterBenchmarkCampaignPostsQuerySchema = z
  .object({
    ...benchmarkPostsQueryBaseShape,
    limit: z.coerce
      .number()
      .refine(v => (PAGE_SIZE_OPTIONS as readonly number[]).includes(v))
      .default(DEFAULT_PAGE_SIZE)
  })
  .superRefine(refineBenchmarkPostsQuery)

function omitKeys<T extends Record<string, unknown>, K extends keyof T>(source: T, keys: readonly K[]): Omit<T, K> {
  const result = { ...source }
  for (const key of keys) delete result[key]
  return result
}

export const twitterBenchmarkCampaignPostsQueryRestoreSchema = z.object({
  // groupId・offset・期間は URL 復元で扱わないため除外する。
  ...omitKeys(benchmarkPostsQueryBaseShape, ['groupId', 'offset', 'startDatetime', 'endDatetime'] as const),
  // 期間はクエリ省略時（＝デフォルト期間）を許容するため optional にする。
  startDatetime: z.string().datetime({ offset: true }).optional(),
  endDatetime: z.string().datetime({ offset: true }).optional(),
  // limit のみ扱う（offset は URL の page から FE 側で算出）。
  limit: z.coerce
    .number()
    .refine(v => (PAGE_SIZE_OPTIONS as readonly number[]).includes(v))
    .default(DEFAULT_PAGE_SIZE)
})

// GET /csv 競合投稿 CSV: フィルタは一覧と同一。limit のみ CSV 用に 1〜CSV_PAGE_SIZE を許可する
export const getTwitterBenchmarkCampaignPostsCsvQuerySchema = z
  .object({
    ...benchmarkPostsQueryBaseShape,
    limit: z.coerce.number().int().positive().max(CSV_PAGE_SIZE).default(CSV_PAGE_SIZE)
  })
  .superRefine(refineBenchmarkPostsQuery)

// GET /:postId 競合投稿詳細
export const getTwitterBenchmarkCampaignPostParamsSchema = z.object({
  postId: z.coerce.number().int().positive()
})

// GET /accounts/search アカウントリモートサーチ
// screenName（前方一致）と accountIds（完全一致・ID→表示名解決用）はどちらか一方を必須とする
export const getAccountsSearchQuerySchema = z
  .object({
    groupId: z.coerce.number().int().positive(),
    screenName: z.string().min(2).optional(),
    accountIds: z.array(z.coerce.number().int().positive()).min(1).max(ACCOUNT_SEARCH_IDS_MAX).optional()
  })
  .refine(query => query.screenName !== undefined || query.accountIds !== undefined, {
    message: 'アカウント検索の値が不正です。'
  })

// グループごとのタグ登録上限。全件取得時の limit 上限も兼ねる（上限まで 1 リクエストで取り切れる）
export const TAG_TOTAL_MAX = 500

export const getTagsQuerySchema = z.object({
  groupId: z.coerce.number().int().positive(),
  sortBy: z.enum(['id']).default('id'),
  order: z.enum(['asc', 'desc']).default('asc'),
  limit: z.coerce.number().int().positive().max(TAG_TOTAL_MAX).default(DEFAULT_PAGE_SIZE),
  offset: z.coerce.number().int().nonnegative().default(0)
})

// タグ名スキーマ（作成・更新で共有: 1〜20 文字・トリム後 1 文字以上）
const tagNameSchema = z
  .string()
  .min(1)
  .max(20)
  .transform(v => v.trim())
  .refine(v => v.length >= 1, {
    message: 'タグ名は空白のみは不可（トリム後 1〜20 文字）'
  })

// POST /tags タグ作成
export const postTagBodySchema = z.object({
  name: tagNameSchema
})

// PUT /tags/:tagId タグ更新
export const putTagParamsSchema = z.object({
  tagId: z.coerce.number().int().positive()
})
export const putTagBodySchema = z.object({
  name: tagNameSchema
})

// DELETE /tags/:tagId タグ削除
export const deleteTagParamsSchema = z.object({
  tagId: z.coerce.number().int().positive()
})

// 1 投稿あたりに付与できるタグ数の上限
export const TAG_PER_POST_MAX = 10

// PUT /:postId/tags タグ付与（単体）
export const putPostTagsParamsSchema = z.object({
  postId: z.coerce.number().int().positive()
})
export const putPostTagsBodySchema = z.object({
  tagIds: z.array(z.number().int().positive())
})

export const postTagsBulkAssignBodySchema = z.object({
  assignments: z
    .array(
      z.object({
        postId: z.number().int().positive(),
        tagIds: z.array(z.number().int().positive()).max(TAG_PER_POST_MAX)
      })
    )
    .min(1)
})

export const TABLE_COLUMN_KEYS = [
  'campaign_type',
  'incentive',
  'winner_count',
  'impressions_count',
  'likes_count',
  'retweets_count',
  'quotes_count',
  'replies_count',
  'reactions_count',
  'reactions_rate',
  'campaign_period',
  'campaign_days',
  'last_synced_at',
  'is_trend',
  'campaign_detail'
] as const

const tableColumnKeySchema = z.enum(TABLE_COLUMN_KEYS)

// 表示列設定の 1 行。保存される行 = 表示中の列。非表示列は永続化しない（is_visible は持たない）。
export const tableSettingsItemSchema = z.object({
  column: tableColumnKeySchema,
  orderNo: z.number().int().nonnegative()
})

export const putTableSettingsBodySchema = z.object({
  // 表示中の列のみを並び順で送る（0〜15 件）。保存行に無い列はリロード時に閉じた（非表示）状態となる。
  settings: z
    .array(tableSettingsItemSchema)
    .max(TABLE_COLUMN_KEYS.length)
    .superRefine((settings, ctx) => {
      const columns = settings.map(s => s.column)
      if (new Set(columns).size !== columns.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: '表示列が重複しています。' })
      }
    })
})

// 共通パーツ
const tagItemSchema = z.object({
  id: z.number().int().positive(),
  name: z.string()
})

const accountItemSchema = z.object({
  id: z.number().int().positive(),
  screenName: z.string(),
  name: z.string(),
  // アカウントアイコン画像 URL（プレビュー列・詳細ドロワーのアカウント表示に使用）
  pictureUrl: z.string(),
  followersCount: z.number().int().nonnegative()
})

const industryItemSchema = z.object({
  id: z.number().int().nonnegative(),
  majorCategory: z.string(),
  minorCategory: z.string()
})

const metricsSchema = z.object({
  impressionsCount: z.number().int().nonnegative(),
  likesCount: z.number().int().nonnegative(),
  retweetsCount: z.number().int().nonnegative(),
  quotesCount: z.number().int().nonnegative(),
  repliesCount: z.number().int().nonnegative(),
  reactionsCount: z.number().int().nonnegative(),
  reactionsRate: z.coerce.number().nonnegative()
})

const postSummarySchema = z.object({
  id: z.number().int().positive(),
  postId: z.string(),
  postText: z.string().nullable(),
  postUrl: z.string().nullable(),
  // 投稿プレビュー画像 URL（プレビュー表示 ON 時のサムネイル・詳細ドロワーのメディア表示に使用）
  previewPostMediaUrl: z.string().nullable(),
  publishDatetime: z.date().nullable(),
  campaignStartDatetime: z.date().nullable(),
  campaignEndDatetime: z.date().nullable(),
  campaignDays: z.number().int().nonnegative().nullable(),
  campaignType: z.number().int(),
  campaignDetail: z.string().nullable(),
  incentive: z.number().int(),
  winnerCount: z.number().int().nonnegative().nullable(),
  isTrend: z.boolean(),
  lastSyncedAt: z.date().nullable()
})

const trendItemSchema = z.object({
  keyword: z.string(),
  highestRanking: z.number().int().positive(),
  firstTrendedAt: z.date(),
  highestRankedAt: z.date()
})

// GET /accounts/search レスポンス
export const accountSearchResponseSchema = z.object({
  accounts: z.array(
    z.object({
      id: z.number().int().positive(),
      screenName: z.string(),
      name: z.string(),
      pictureUrl: z.string()
    })
  )
})

// GET /tags レスポンス
export const tagListResponseSchema = z.object({
  tags: z.array(
    z.object({
      id: z.number().int().positive(),
      name: z.string(),
      assignedCount: z.number().int().nonnegative()
    })
  ),
  totalCount: z.number().int().nonnegative()
})

// GET / 競合投稿一覧 レスポンス
const postListItemSchema = z.object({
  post: postSummarySchema,
  account: accountItemSchema.nullable(),
  industry: industryItemSchema.nullable(),
  metrics: metricsSchema,
  tags: z.array(tagItemSchema)
})

export const postListResponseSchema = z.object({
  posts: z.array(postListItemSchema),
  totalCount: z.number().int().nonnegative()
})

// GET /:postId 競合投稿詳細 レスポンス
const postDetailItemSchema = z.object({
  post: postSummarySchema,
  account: accountItemSchema.nullable(),
  industry: industryItemSchema.nullable(),
  metrics: metricsSchema,
  tags: z.array(tagItemSchema),
  trends: z.array(trendItemSchema)
})

export const postDetailResponseSchema = z.object({
  post: postDetailItemSchema,
  totalCount: z.literal(1)
})

const postCsvItemSchema = z.object({
  post: postSummarySchema,
  account: accountItemSchema.nullable(),
  industry: industryItemSchema.nullable(),
  metrics: metricsSchema,
  tags: z.array(tagItemSchema),
  // 全 word 中の最小 ranking（＝最高到達順位）と、その順位を達成した keyword
  highestTrendRanking: z.number().int().positive().nullable(),
  highestTrendKeyword: z.string().nullable()
})

export const postCsvResponseSchema = z.object({
  posts: z.array(postCsvItemSchema),
  totalCount: z.number().int().nonnegative()
})

// POST /tags タグ作成 レスポンス
export const postTagResponseSchema = z.object({
  tag: tagItemSchema
})

// PUT /tags/:tagId タグ更新 レスポンス
export const putTagResponseSchema = z.object({
  data: z.literal(true)
})

// DELETE /tags/:tagId タグ削除 レスポンス
export const deleteTagResponseSchema = z.object({
  releasedCount: z.number().int().nonnegative()
})

// DELETE /:postId/tags/assign タグ解除（単体） レスポンス
export const deleteTagsBulkAssignResponseSchema = z.object({
  removedCount: z.number().int().nonnegative()
})

// POST /tags/bulk-assign タグ付与（一括・全置換） レスポンス
export const postTagsBulkAssignResponseSchema = z.object({
  data: z.literal(true)
})

// GET /industry 業界マスタ レスポンス
export const industryListResponseSchema = z.object({
  industries: z.array(industryItemSchema)
})

export const tableSettingsResponseSchema = z.object({
  settings: z.array(tableSettingsItemSchema)
})

export type GetTwitterBenchmarkCampaignPostsQuery = z.infer<typeof getTwitterBenchmarkCampaignPostsQuerySchema>
export type GetTwitterBenchmarkCampaignPostsCsvQuery = z.infer<typeof getTwitterBenchmarkCampaignPostsCsvQuerySchema>

export type TwitterBenchmarkCampaignPostsQueryRestore = z.infer<typeof twitterBenchmarkCampaignPostsQueryRestoreSchema>
export type SortKey = GetTwitterBenchmarkCampaignPostsQuery['sortBy']
export type SearchFilters = Omit<
  GetTwitterBenchmarkCampaignPostsQuery,
  'groupId' | 'sortBy' | 'order' | 'limit' | 'offset'
>

// 表示列のキー（TABLE_COLUMN_KEYS の要素型）。FE columns.ts・ストアのキー型として共有
export type ColumnKey = (typeof TABLE_COLUMN_KEYS)[number]
export type TableSettingsItem = z.infer<typeof tableSettingsItemSchema>
export type PutTableSettingsBody = z.infer<typeof putTableSettingsBodySchema>

export type GetTwitterBenchmarkCampaignPostParams = z.infer<typeof getTwitterBenchmarkCampaignPostParamsSchema>
export type GetAccountsSearchQuery = z.infer<typeof getAccountsSearchQuerySchema>
export type GetTagsQuery = z.infer<typeof getTagsQuerySchema>
export type PostTagBody = z.infer<typeof postTagBodySchema>
export type PutTagParams = z.infer<typeof putTagParamsSchema>
export type PutTagBody = z.infer<typeof putTagBodySchema>
export type DeleteTagParams = z.infer<typeof deleteTagParamsSchema>
export type PutPostTagsParams = z.infer<typeof putPostTagsParamsSchema>
export type PutPostTagsBody = z.infer<typeof putPostTagsBodySchema>
export type PostTagsBulkAssignBody = z.infer<typeof postTagsBulkAssignBodySchema>

export type AccountSearchResponse = z.infer<typeof accountSearchResponseSchema>
export type TagListResponse = z.infer<typeof tagListResponseSchema>
export type PostListResponse = z.infer<typeof postListResponseSchema>
export type PostDetailResponse = z.infer<typeof postDetailResponseSchema>
export type PostCsvResponse = z.infer<typeof postCsvResponseSchema>
export type PostTagResponse = z.infer<typeof postTagResponseSchema>
export type PutTagResponse = z.infer<typeof putTagResponseSchema>
export type DeleteTagResponse = z.infer<typeof deleteTagResponseSchema>
export type DeleteTagsBulkAssignResponse = z.infer<typeof deleteTagsBulkAssignResponseSchema>
export type PostTagsBulkAssignResponse = z.infer<typeof postTagsBulkAssignResponseSchema>
export type IndustryListResponse = z.infer<typeof industryListResponseSchema>
export type TableSettingsResponse = z.infer<typeof tableSettingsResponseSchema>
